import React, { useEffect } from "react";
import { Send, ImageUp, Globe, Brain, Zap } from "lucide-react";
import { Tag, Image } from "antd";
import { useRouter, usePathname } from "next/navigation";

const MessageSender = ({
  value,
  header = "Send message to ChatGPT",
  onSubmit,
  onChange,
  loading = false,
  className = "",
  changeNetwork,
  changeDeep,
  changeGenius,
  networkText,
  typeMessageText,
  isCustomMade,
  isSwitch,
  isSwitchDeep,
  isSwitchGenius,
  handleImageUploadCallback,
  isProUser,
}) => {
  const [checked, setChecked] = React.useState(false);
  const [deepChecked, setDeepChecked] = React.useState(false);
  const [geniusChecked, setGeniusChecked] = React.useState(false);
  const [isComposing, setIsComposing] = React.useState(false); // 用于标识是否正在使用输入法

  const router = useRouter();
  const pathname = usePathname();

  useEffect(() => {
    setChecked(isSwitch);
    changeNetwork(isSwitch);
  }, [isSwitch]);

  useEffect(() => {
    setDeepChecked(isSwitchDeep);
    changeDeep(isSwitchDeep);
  }, [isSwitchDeep]);

  useEffect(() => {
    setGeniusChecked(isSwitchGenius);
    changeGenius?.(isSwitchGenius);
  }, [isSwitchGenius]);

  const handleToggle = () => {
    const newChecked = !checked;
    setChecked(newChecked);
    changeNetwork(newChecked);
  };

  const handleDeepToggle = () => {
    const newChecked = !deepChecked;
    setDeepChecked(newChecked);
    changeDeep(newChecked);
  };

  const handleGeniusToggle = () => {
    const newChecked = !geniusChecked;
    setGeniusChecked(newChecked);
    changeGenius?.(newChecked);
    if (!isProUser) {
      setGeniusChecked(false);
      changeGenius?.(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (value.trim()) {
      onSubmit?.(value.trim());
    }
  };

  const handleButtonClick = (e) => {
    setIsUpload(false);
    setImageSrc("");
    e?.preventDefault();
    if (value.trim()) {
      onSubmit?.(value.trim());
    }
  };

  const textareaRef = React.useRef(null);

  // Auto-resize function
  const adjustHeight = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = "auto";

    // Calculate new height (with max-height of 200px)
    const newHeight = Math.min(textarea.scrollHeight, 200);
    textarea.style.height = `${newHeight}px`;
  };

  // Adjust height on value change
  React.useEffect(() => {
    adjustHeight();
  }, [value]);

  const headers = {
    // "Content-Type": "multipart/form-data",
    Authorization:
      typeof window !== "undefined" && localStorage.getItem("token")
        ? `Bearer ${localStorage.getItem("token")}`
        : "",
  };

  const handleImageUpload = async (e: any) => {
    const files = e.target.files;
    if (!files || files.length === 0) {
      console.error("未选择文件");
      return;
    }
    setIsUpload(true)
    const formData = new FormData();
    const fileArray = Array.from(files); // 解决 forEach 报错的问题
    fileArray.forEach((file) => {
      // 可以在这里添加文件类型、大小校验
      if (file.size > 5 * 1024 * 1024) {
        console.error("文件大小超过 5MB 限制");
        return;
      }
      formData.append("file", file);
    });

    try {
      const response = await fetch(
        "https://api.heckai.weight-wave.com/api/ha/img/upload",
        {
          method: "POST",
          headers,
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error("图片上传失败");
      }

      const data = await response.json();
      console.log("上传成功:", data);
      setImageSrc(data.imageUrl);
      handleImageUploadCallback && handleImageUploadCallback(data.imageUrl);
      // 处理上传后的图片 URL
    } catch (error) {
      console.error("上传出错:", error);
      if (error.status === 401) {
        router.push(`/login?from=${encodeURIComponent(pathname)}`);
      }
    } finally {
      e.target.value = "";
    }
  };

  const [imageSrc, setImageSrc] = React.useState<string | null>(null);
  const [isUpload, setIsUpload] = React.useState(false);

  return (
    <div className={`w-full ${className}`}>
      {/* bg-[#F4F4F4] p-4 */}
      <div className="p-4 rounded-2xl flex w-full cursor-text flex-col rounded-3xl border border-token-border-light duration-150 ease-in-out contain-inline-size motion-safe:transition-[color,background-color,border-color,text-decoration-color,fill,stroke,box-shadow] shadow-[0_9px_9px_0px_rgba(0,0,0,0.01),_0_2px_5px_0px_rgba(0,0,0,0.06)] has-[:focus]:shadow-[0_2px_12px_0px_rgba(0,0,0,0.04),_0_9px_9px_0px_rgba(0,0,0,0.01),_0_2px_5px_0px_rgba(0,0,0,0.06)] bg-token-main-surface-primary custom-wapper">
        {/* <div className="mb-2 text-gray-600 text-sm">{header}</div> */}
        {isUpload && <div className="mb-2 text-gray-600 text-sm">
          {imageSrc ? (
            <Image width={80} src={imageSrc} />
          ) : (
            <div style={{ width: "80px" }}>Uploading...</div>
          )}
        </div>}
        <form onSubmit={handleSubmit} className="relative">
          <div className="mt-2 flex items-center">
            <textarea
              value={value}
              onChange={(e) => onChange?.(e.target.value)}
              className="flex-1 resize-none pb-2 focus:outline-none bg-transparent text-black placeholder-gray-400"
              placeholder={typeMessageText}
              onCompositionStart={() => setIsComposing(true)} // 开始输入时标记正在使用输入法
              onCompositionEnd={() => setIsComposing(false)} // 输入结束时标记输入法已确认
              style={{
                maxHeight: "200px",
                minHeight: "24px",
              }}
              ref={textareaRef}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey && !isComposing) {
                  e.preventDefault();
                  handleButtonClick();
                }
              }}
            />
          </div>
          <div
            className="flex justify-between items-center"
            style={
              isCustomMade
                ? { justifyContent: "space-between" }
                : { justifyContent: "flex-end" }
            }
          >
            <div
              className="flex items-center space-x-2 pt-2"
              style={!isCustomMade ? { display: "none" } : { display: "flex" }}
            >
              {/* 添加一个加号的图标 */}
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={(e) => handleImageUpload(e)}
                style={{ display: "none" }}
                id="image-upload"
              />
              <label htmlFor="image-upload">
                <div className="text-gray-600 cursor-pointer">
                  {/* <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4v16m8-8H4"
                    />
                  </svg> */}
                  <ImageUp />
                </div>
              </label>
              {/* <div className="text-gray-600">
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"
                  />
                </svg>
              </div>
              <span className="text-sm text-gray-600">{networkText}</span> */}
              <div>
                <Tag
                  onClick={handleToggle}
                  color={checked ? "blue" : "default"}
                  style={{ 
                    cursor: "pointer",
                    padding: "8px 16px",
                    fontSize: "14px",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    borderRadius: "20px",
                    backgroundColor: "white",
                    border: checked ? "1px solid #1677ff" : "1px solid #d9d9d9",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.05)"
                  }}
                >
                  <Globe size={16} color={checked ? "#1677ff" : "#666666"} />
                  <span style={{ color: checked ? "#1677ff" : "#666666" }}>{networkText}</span>
                </Tag>
              </div>
              {/* <span className="text-sm text-gray-600">DeepThink</span> */}
              <div>
                <Tag
                  onClick={handleDeepToggle}
                  color={deepChecked ? "blue" : "default"}
                  style={{ 
                    cursor: "pointer",
                    padding: "8px 16px",
                    fontSize: "14px",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    borderRadius: "20px",
                    backgroundColor: "white",
                    border: deepChecked ? "1px solid #1677ff" : "1px solid #d9d9d9",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.05)"
                  }}
                >
                  <Brain size={16} color={deepChecked ? "#1677ff" : "#666666"} />
                  <span style={{ color: deepChecked ? "#1677ff" : "#666666" }}>Think</span>
                </Tag>
              </div>
              <div>
                <Tag
                  onClick={handleGeniusToggle}
                  color={geniusChecked ? "blue" : "default"}
                  style={{ 
                    cursor: "pointer",
                    padding: "8px 16px",
                    fontSize: "14px",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    borderRadius: "20px",
                    backgroundColor: "white",
                    border: geniusChecked ? "1px solid #1677ff" : "1px solid #d9d9d9",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.05)"
                  }}
                >
                  <Zap size={16} color={geniusChecked ? "#1677ff" : "#666666"} />
                  <span style={{ color: geniusChecked ? "#1677ff" : "#666666" }}>Genius</span>
                </Tag>
              </div>
            </div>
            <button
              type="button"
              onClick={handleButtonClick}
              disabled={loading || !value.trim()}
              className="ml-2 rounded-lg bg-black hover:bg-gray-800 disabled:bg-gray-400 transition-colors aspect-square w-[32px] h-[32px] flex items-center justify-center"
            >
              <Send className="w-5 h-5 text-white" />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MessageSender;
